'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '@/app/lib/posthog';
import { useTheme } from '@/app/Components/ThemeProvider';
import AskSection from './AskSection';
import DitherHero from './DitherHero';
import { HERO_VARIANTS, isHeroVariant, probeHeroVideo, type HeroVariant, type HeroVideoKind } from './heroVariants';
import FeatureWordScroll from './FeatureWordScroll';
import LandingIcon from './LandingIcon';
import styles from './LandingHome.module.css';
import { useSnapshots } from './landingData';

function InsightTicker() {
  const data = useSnapshots('home');
  const insights = data?.home.insights ?? [];
  return (
    <div className={styles.ticker} aria-label="Scarlet Sync by the numbers">
      <div className={styles.lane} style={{ ['--speed' as string]: '90s' }}>
        {[0, 1].map((copy) => insights.map(([value, label]) => (
          <span key={`${copy}-${label}`} aria-hidden={copy === 1 || undefined}><b>{value}</b>{label}</span>
        )))}
        {!insights.length ? <span>&nbsp;</span> : null}
      </div>
    </div>
  );
}

function FinalCta() {
  const data = useSnapshots('home');
  const features = data?.home.features ?? [];
  return (
    <section className={styles.final} aria-labelledby="locked-in">
      <h2 id="locked-in" className={styles.finalTitle}>RU locked<br /><span>in?</span></h2>
      <div className={styles.ctas}>
        <Link className={styles.btnPrimary} href="/scheduling" onClick={() => trackEvent('landing_cta_click', { cta: 'start_planning_free', location: 'bottom_cta' })}>Start planning for free</Link>
        <Link className={styles.btnGhost} href="/support/developer-connectors" onClick={() => trackEvent('landing_cta_click', { cta: 'connect_ai', location: 'bottom_cta' })}>Connect ChatGPT or Claude</Link>
      </div>
      <nav className={styles.features} aria-label="All Scarlet Sync features">
        <div className={`${styles.lane} ${styles.featureLane}`} style={{ ['--speed' as string]: '60s' }}>
          {[0, 1].map((copy) => features.map((f) => (
            <Link
              key={`${copy}-${f.href}`}
              href={`/${f.href}`}
              tabIndex={copy === 1 ? -1 : undefined}
              aria-hidden={copy === 1 || undefined}
              onClick={() => trackEvent('landing_cta_click', { cta: f.icon, location: 'feature_marquee' })}
            >
              <LandingIcon name={f.icon} />{f.name}
            </Link>
          )))}
        </div>
      </nav>
    </section>
  );
}

/** Guest homepage: dithered campus hero, insights ticker, feature word scroll, AI section and feature marquee. */
/**
 * The hero-background switcher is a review tool, not part of the page: it shows
 * in local development, or anywhere when `?hero=` is in the URL.
 */
function showHeroSwitcher() {
  return process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).has('hero');
}

export default function LandingHome() {
  const { theme } = useTheme();
  const light = theme === 'light';
  const rootRef = useRef<HTMLDivElement>(null);
  const [variant, setVariant] = useState<HeroVariant | null>(null);
  const [videoKind, setVideoKind] = useState<HeroVideoKind>(null);
  const [heroSwitcher, setHeroSwitcher] = useState(false);
  useEffect(() => setHeroSwitcher(showHeroSwitcher()), []);
  // Each page load picks a hero background at random (?hero=buses|day|constellation|video forces one).
  useEffect(() => {
    let cancelled = false;
    probeHeroVideo().then((kind) => {
      if (cancelled) return;
      setVideoKind(kind);
      const forced = new URLSearchParams(window.location.search).get('hero');
      const pool: HeroVariant[] = ['buses', 'day', 'constellation', ...(kind ? (['video'] as const) : [])];
      setVariant(isHeroVariant(forced) ? forced : pool[Math.floor(Math.random() * pool.length)]);
    });
    return () => { cancelled = true; };
  }, []);
  // The sidebar takes on the page's dominant colour (every section paints the root's solid background
  // and the hero is mostly that colour), so the rail reads as part of the page rather than a foreign strip.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const html = document.documentElement;
    html.style.setProperty('--ss-landing-bg', getComputedStyle(el).backgroundColor);
    html.dataset.landing = 'true';
    return () => { html.removeAttribute('data-landing'); html.style.removeProperty('--ss-landing-bg'); };
  }, [light]);
  return (
    <div ref={rootRef} className={styles.root} data-look={light ? 'paper' : undefined}>
      <DitherHero variant={variant} videoKind={videoKind} onVideoFail={() => { setVideoKind(null); }} />
      {heroSwitcher ? (
        <div className={styles.heroDebug} role="group" aria-label="Hero background (debug)">
          {HERO_VARIANTS.map(([id, label]) => (
            <button key={id} type="button" aria-pressed={variant === id} className={variant === id ? styles.heroDebugOn : undefined} onClick={() => setVariant(id)}>{label}</button>
          ))}
        </div>
      ) : null}
      <InsightTicker />
      <FeatureWordScroll />
      <AskSection />
      <FinalCta />
    </div>
  );
}
